import { io, type Socket } from 'socket.io-client';
import { getToken } from './api';

let socket: Socket | null = null;

const RECONNECT_DELAY_MIN = 1_000;
const RECONNECT_DELAY_MAX = 30_000;
const RECONNECT_ATTEMPTS = 10;

/**
 * Initialize the socket connection with JWT authentication.
 * Reuses existing connection if already established.
 */
export function connectSocket(): Socket {
  if (socket?.connected) {
    return socket;
  }

  const token = getToken();

  socket = io(window.location.origin, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: RECONNECT_ATTEMPTS,
    reconnectionDelay: RECONNECT_DELAY_MIN,
    reconnectionDelayMax: RECONNECT_DELAY_MAX,
    timeout: 10_000,
    autoConnect: true,
  });

  socket.on('connect', () => {
    console.info('[Socket] Connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    console.info('[Socket] Disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error.message);
    if (error.message === 'Authentication error') {
      disconnectSocket();
    }
  });

  return socket;
}

/**
 * Disconnect and clean up the socket.
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

/**
 * Get the current socket instance (may be null).
 */
export function getSocket(): Socket | null {
  return socket;
}

// ─── Room management ────────────────────────────────────────

/**
 * Join a room (e.g., base-specific or flight-specific channel).
 */
export function joinRoom(room: string): void {
  socket?.emit('room:join', room);
}

/**
 * Leave a room.
 */
export function leaveRoom(room: string): void {
  socket?.emit('room:leave', room);
}

// ─── Typed event helpers ────────────────────────────────────

type EventCallback<T = unknown> = (data: T) => void;

/**
 * Subscribe to a typed socket event. Returns an unsubscribe function.
 */
export function onEvent<T = unknown>(event: string, callback: EventCallback<T>): () => void {
  socket?.on(event, callback as (...args: unknown[]) => void);
  return () => {
    socket?.off(event, callback as (...args: unknown[]) => void);
  };
}

/**
 * Emit a typed event to the server.
 */
export function emitEvent<T = unknown>(event: string, data?: T): void {
  socket?.emit(event, data);
}
